import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import MuiDivider from '@mui/material/Divider';
import MuiInputAdornment from '@mui/material/InputAdornment';
import MuiMenuItem from '@mui/material/MenuItem';
import MuiSelect from '@mui/material/Select';
import MuiTextField from '@mui/material/TextField';
import MuiTypography from '@mui/material/Typography';
import { isValidPhoneNumber, validatePhoneNumberLength } from 'libphonenumber-js/core'; // eslint-disable-line import/no-internal-modules
import React, { forwardRef, useEffect, useMemo, useRef, useState } from 'react';
import { defaultCountries, FlagImage, parseCountry, usePhoneInput } from 'react-international-phone';
import { useMergeRefs } from '../hooks';
import { styled } from '../styled';
import { fieldErrorProps, FieldSkeleton } from '../textfield/utils';
import { firstLetterUppercase } from '../typography';
import { withVersion } from '../utils';
const DEFAULT_ERROR_MESSAGES = {
    NOT_A_NUMBER: 'Not a valid number',
    INVALID_COUNTRY: 'Invalid country code',
    TOO_SHORT: 'Phone number is too short',
    TOO_LONG: 'Phone number is too long',
    INVALID_LENGTH: 'Invalid phone number length',
    INVALID_NUMBER: 'Invalid phone number',
    REQUIRED: 'Phone number is required'
};
let phoneMetadata;
let metadataLoading;
const loadMetadata = async () => {
    if (!metadataLoading) {
        metadataLoading = (async () => {
            try {
                // eslint-disable-next-line import/no-internal-modules
                const m = await import('libphonenumber-js/max/metadata');
                phoneMetadata = m.default;
            }
            catch (error) {
                metadataLoading = undefined;
                if (process.env.NODE_ENV === 'development') {
                    console.error('PhoneField: failed to load validation metadata', error);
                }
            }
        })();
    }
    return metadataLoading;
};
const isOnlyDialCode = (phone, dialCode) => {
    const phoneDigits = phone.replace(/\D/g, '');
    const dialCodeDigits = dialCode.replace(/\D/g, '');
    return phoneDigits.length <= dialCodeDigits.length;
};
const validatePhone = (phone) => {
    if (!phoneMetadata) {
        return undefined;
    }
    const lengthResult = validatePhoneNumberLength(phone, phoneMetadata);
    if (lengthResult) {
        return lengthResult;
    }
    if (!isValidPhoneNumber(phone, phoneMetadata)) {
        return 'INVALID_NUMBER';
    }
    return undefined;
};
const CountrySelect = styled(MuiSelect)(({ theme }) => ({
    width: theme.spacing(7)
}));
const MenuItemFlagImage = styled(FlagImage)(({ theme }) => ({
    width: theme.spacing(3),
    marginRight: theme.spacing(1),
    flexShrink: 0
}));
const SelectedFlagImage = styled(FlagImage)(({ theme }) => ({
    width: theme.spacing(3),
    display: 'block',
    paddingLeft: theme.spacing(0.5),
    paddingTop: theme.spacing(0.5)
}));
const CountryName = styled(MuiTypography)(({ theme }) => ({
    marginRight: theme.spacing(1),
    flex: 1
}));
const CountryAdornment = styled(MuiInputAdornment)(({ theme }) => ({
    marginRight: theme.spacing(0.25),
    marginLeft: theme.spacing(-1)
}));
const PhoneTextField = styled(MuiTextField)(({ $width }) => ({
    width: $width
}));
const buildCountryMenuItems = (countries, preferredCountries) => {
    const renderItem = (parsed) => (_jsxs(MuiMenuItem, { value: parsed.iso2, children: [_jsx(MenuItemFlagImage, { iso2: parsed.iso2 }), _jsx(CountryName, { variant: "body2", children: parsed.name }), _jsxs(MuiTypography, { variant: "body2", color: "text.secondary", children: ["+", parsed.dialCode] })] }, parsed.iso2));
    if (!preferredCountries.length) {
        return countries.map((country) => renderItem(parseCountry(country)));
    }
    const preferredSet = new Set(preferredCountries);
    const preferredList = [];
    const restList = [];
    for (const country of countries) {
        const parsed = parseCountry(country);
        if (preferredSet.has(parsed.iso2)) {
            preferredList.push(parsed);
        }
        else {
            restList.push(parsed);
        }
    }
    preferredList.sort((a, b) => preferredCountries.indexOf(a.iso2) -
        preferredCountries.indexOf(b.iso2));
    return [
        ...preferredList.map(renderItem),
        _jsx(MuiDivider, {}, "divider"),
        ...restList.map(renderItem)
    ];
};
const PhoneFieldImpl = forwardRef((props, ref) => {
    const { value = '', error, skeleton = false, size = 'small', width: _width, variant = 'outlined', defaultCountry = 'de', preferredCountries = [], countries: countriesProp, forceDialCode = false, disableCountryGuess = false, disableFormatting = false, validate = false, required = false, onErrorChange, onChange, onBlur, helperText, slotProps, disabled, ...muiProps } = props;
    const width = _width || '100%';
    const countries = countriesProp ?? defaultCountries;
    const textFieldRef = useMergeRefs(ref);
    const [internalError, setInternalError] = useState(undefined);
    const touchedRef = useRef(false);
    const valueOnFocusRef = useRef('');
    const lastErrorRef = useRef(undefined);
    useEffect(() => {
        if (validate) {
            void loadMetadata();
        }
    }, [validate]);
    const fireErrorChange = (errorKey) => {
        if (lastErrorRef.current !== errorKey) {
            lastErrorRef.current = errorKey;
            onErrorChange?.(errorKey);
        }
    };
    const countryMenuItems = useMemo(() => buildCountryMenuItems(countries, preferredCountries), [countries, preferredCountries]);
    const { inputValue, country, setCountry, handlePhoneValueChange, inputRef } = usePhoneInput({
        defaultCountry,
        value,
        countries,
        forceDialCode,
        disableCountryGuess,
        disableFormatting,
        onChange: (data) => {
            const onlyDialCode = isOnlyDialCode(data.phone, data.country.dialCode);
            if ((required || validate) && touchedRef.current) {
                if (required && onlyDialCode) {
                    setInternalError('REQUIRED');
                    fireErrorChange('REQUIRED');
                }
                else {
                    setInternalError(undefined);
                    fireErrorChange(undefined);
                }
            }
            else if (required) {
                fireErrorChange(onlyDialCode ? 'REQUIRED' : undefined);
            }
            onChange?.(data.phone, data.country);
        }
    });
    const latestValueRef = useRef(inputValue);
    useEffect(() => {
        latestValueRef.current = inputValue;
    }, [inputValue]);
    useEffect(() => {
        if (required && isOnlyDialCode(value, country.dialCode)) {
            fireErrorChange('REQUIRED');
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);
    const handleBlur = async (e) => {
        const phone = e.target.value;
        if (phone === valueOnFocusRef.current) {
            onBlur?.(e);
            return;
        }
        touchedRef.current = true;
        let errorKey;
        if (required && isOnlyDialCode(phone, country.dialCode)) {
            errorKey = 'REQUIRED';
        }
        else if (validate && !isOnlyDialCode(phone, country.dialCode)) {
            await loadMetadata();
            if (phone !== latestValueRef.current) {
                return;
            }
            errorKey = validatePhone(phone);
        }
        setInternalError(errorKey);
        fireErrorChange(errorKey);
        onBlur?.(e);
    };
    if (skeleton) {
        return (_jsx(FieldSkeleton, { width: width, children: _jsx(MuiTextField, { size: size, variant: variant, fullWidth: true, disabled: true }) }));
    }
    const menuPaperWidth = textFieldRef.current?.clientWidth ?? 'auto';
    const displayError = error ??
        (internalError ? DEFAULT_ERROR_MESSAGES[internalError] : undefined);
    const countrySelector = (_jsx(CountryAdornment, { position: "start", children: _jsx(CountrySelect, { variant: "standard", disableUnderline: true, size: "small", value: country.iso2, disabled: disabled, onChange: (e) => setCountry(e.target.value), renderValue: (val) => (_jsx(SelectedFlagImage, { iso2: val })), MenuProps: {
                anchorEl: textFieldRef.current,
                anchorOrigin: {
                    vertical: 'bottom',
                    horizontal: 'left'
                },
                transformOrigin: {
                    vertical: 'top',
                    horizontal: 'left'
                },
                PaperProps: {
                    sx: {
                        maxHeight: '300px',
                        width: menuPaperWidth
                    }
                }
            }, children: countryMenuItems }) }));
    return (_jsx(PhoneTextField, { ref: textFieldRef, "$width": width, size: size, variant: variant, value: inputValue, required: required, ...fieldErrorProps(displayError, helperText), type: "tel", disabled: disabled, onChange: handlePhoneValueChange, onFocus: (e) => {
            valueOnFocusRef.current = e.target.value;
        }, onBlur: handleBlur, inputRef: inputRef, "data-role": "phone-field", slotProps: {
            ...slotProps,
            inputLabel: {
                ...slotProps?.inputLabel,
                shrink: true,
                sx: {
                    ...firstLetterUppercase
                }
            },
            input: {
                ...slotProps?.input,
                startAdornment: countrySelector
            },
            formHelperText: {
                ...slotProps?.formHelperText,
                sx: {
                    ...firstLetterUppercase
                }
            }
        }, ...muiProps }));
});
export const PhoneField = withVersion(PhoneFieldImpl, '1.0.0');
